import { Email, Person, Phone, Work } from '@mui/icons-material';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { createBusinessContact } from 'lib/api/contacts';
import { Contact } from 'lib/api/contacts/types';
import CustomButton from 'lib/designSystem/Button';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import React from 'react';
import { useDispatch } from 'react-redux';

const AddConfirmationDialog = ({
  open,
  setOpen,
  contact,
  onSuccess,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  contact: Contact;
  onSuccess: () => void;
}) => {
  const dispatch = useDispatch();
  const { mutate: createContactFn, isPending } =
    createBusinessContact(dispatch);

  const handleConfirm = () => {
    const { name, email, phone, role, customRole, businessIdentifier } =
      contact;
    const body = {
      name,
      email,
      phone,
      role,
      customRole,
      businessIdentifier,
    };
    const filteredBody = Object.fromEntries(
      Object.entries(body).filter(([, value]) => value != null),
    ) as typeof body;
    createContactFn(filteredBody, {
      onSuccess: () => {
        setOpen(false);
        onSuccess();
      },
    });
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle
        onClose={handleCancel}
        title="Agregar contacto"
        icon={<Person color="primary" />}
        color="primary"
      />
      <DialogContent>
        <Box py={1}>
          <Paper
            sx={{
              p: 1.5,
              boxShadow: 'none',
              border: '1px solid #e0e0e0',
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '1rem',
                  lineHeight: 1.1,
                }}
              >
                Confirmar Agregar Contacto
              </Typography>
              <Chip
                label="Detalles"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, fontSize: '0.75rem' }}
            >
              ¿Estás seguro que deseas agregar este contacto a la empresa?
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                border: '1px solid #f0f0f0',
                backgroundColor: '#fafafa',
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        borderRadius: '50%',
                        p: 0.3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Person
                        sx={{ fontSize: '0.7rem', color: 'primary.main' }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.6rem' }}
                      >
                        Nombre
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ fontSize: '0.7rem' }}
                      >
                        {contact.name}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        borderRadius: '50%',
                        p: 0.3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Email
                        sx={{ fontSize: '0.7rem', color: 'primary.main' }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.6rem' }}
                      >
                        Email
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ fontSize: '0.7rem' }}
                      >
                        {contact.email}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {contact.phone && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          backgroundColor: 'rgba(25, 118, 210, 0.1)',
                          borderRadius: '50%',
                          p: 0.3,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Phone
                          sx={{ fontSize: '0.7rem', color: 'primary.main' }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.6rem' }}
                        >
                          Teléfono
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{ fontSize: '0.7rem' }}
                        >
                          {contact.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {(contact.role || contact.customRole) && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          backgroundColor: 'rgba(25, 118, 210, 0.1)',
                          borderRadius: '50%',
                          p: 0.3,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Work
                          sx={{ fontSize: '0.7rem', color: 'primary.main' }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.6rem' }}
                        >
                          Rol
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{ fontSize: '0.7rem' }}
                        >
                          {contact.role === 'Otro' && contact.customRole
                            ? contact.customRole
                            : contact.role || contact.customRole}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <CustomButton
          color="secondary"
          onClick={handleCancel}
          disabled={isPending}
          variant="outlined"
          size="small"
        >
          Cancelar
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={handleConfirm}
          disabled={isPending}
          loading={isPending}
          size="small"
        >
          Agregar contacto
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddConfirmationDialog;
